import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import {
  AttachmentMetaDataType,
  AttachmentType,
} from '../../../../shared/editor/types';
import { RootState } from '../../../../store/root-reducer';
import { imageUploadRequest } from '../../../sequence/extra-actions';

import { Template } from '../../../templates/types/templates';
import {
  deleteAttachmentUniboxRequest,
  forwardEmailRequest,
  getEmailThreadRequest,
  replyDraftDiscardRequest,
  replyDraftRequest,
  replyEmailRequest,
  replyScheduleRequest,
  uploadAttachmentUniboxRequest,
} from '../../extra-actions';
import { getFieldsRequest } from '../../../sequence/shared/modals/email-modal/test-email/extra-actions';
import {
  EmailThreadFilters,
  ForwardEmailPayload,
  ReplyEmailPayload,
  ReplyForwardEditorAction,
  ReplyForwardEmailPreview,
  DraftStatus,
  UnifiedSchedule,
  AttachmentUploadUniboxPayload,
  AttachmentDeleteUniboxPayload,
  ReplyDraftPayload,
  ReplySchedulePayload,
  ReplyDraftDiscardPayload,
} from '../../type';
import ReplyForwardEditor from './reply-forward-editor';
import {
  resetDeleteAttachmentUniboxRequest,
  resetReplyDraftDiscardRequest,
  resetReplyForwardDraftRequest,
  resetUploadAttachmentUniboxRequest,
  updateUniboxEmailScheduleData,
} from '../../unified-inbox-slice';
import { resetSequenceTimezoneData } from '../../../sequence/sequence-slice';
import { UnifiedInboxFeed } from '../../enum/unified-inbox.enum';

const mapStateToProps = (
  state: RootState,
  ownProps: { selectedEmail: ReplyForwardEmailPreview },
) => {
  const emailId = Math.abs(Number(ownProps?.selectedEmail?.emailId));

  return {
    // Reply Email request
    replyEmailRequestStatus:
      state.unifiedInbox.replyEmailRequest[emailId]?.status,
    replyEmailRequestMessage:
      state.unifiedInbox.replyEmailRequest[emailId]?.message,
    replyEmailRequestError:
      state.unifiedInbox.replyEmailRequest[emailId]?.error,

    // Forward Email request
    forwardEmailRequestStatus:
      state.unifiedInbox.forwardEmailRequest[emailId]?.status,
    forwardEmailRequestMessage:
      state.unifiedInbox.forwardEmailRequest[emailId]?.message,
    forwardEmailRequestError:
      state.unifiedInbox.forwardEmailRequest[emailId]?.error,

    // Reply Draft request
    replyDraftRequestStatus:
      state.unifiedInbox.replyDraftRequest[emailId]?.status,
    replyDraftRequestMessage:
      state.unifiedInbox.replyDraftRequest[emailId]?.message,
    replyDraftRequestError:
      state.unifiedInbox.replyDraftRequest[emailId]?.error,

    // upload attachment request
    uploadAttachmentRequestStatus:
      state.unifiedInbox.uploadAttachmentUniboxRequest[emailId]?.status,
    uploadAttachmentRequestMessage:
      state.unifiedInbox.uploadAttachmentUniboxRequest[emailId]?.message,
    uploadAttachmentRequestError:
      state.unifiedInbox.uploadAttachmentUniboxRequest[emailId]?.error,
    uploadedAttachment: state.unifiedInbox.uploadedAttachment[emailId],

    // delete attachment request
    deleteAttachmentRequestStatus:
      state.unifiedInbox.deleteAttachmentUniboxRequest[emailId]?.status,
    deleteAttachmentRequestMessage:
      state.unifiedInbox.deleteAttachmentUniboxRequest[emailId]?.message,
    deleteAttachmentRequestError:
      state.unifiedInbox.deleteAttachmentUniboxRequest[emailId]?.error,
    deletedAttachmentId: state.unifiedInbox.deletedAttachmentId[emailId],

    // Reply Draft Discard Request
    replyDraftDiscardRequestStatus:
      state.unifiedInbox.replyDraftDiscardRequest[emailId]?.status,
    replyDraftDiscardRequestMessage:
      state.unifiedInbox.replyDraftDiscardRequest[emailId]?.message,
    replyDraftDiscardRequestError:
      state.unifiedInbox.replyDraftDiscardRequest[emailId]?.error,

    // Reply Schedule Request
    replyScheduleRequestStatus:
      state.unifiedInbox.replyScheduleRequest[emailId]?.status,
    replyScheduleRequestMessage:
      state.unifiedInbox.replyScheduleRequest[emailId]?.message,
    replyScheduleRequestError:
      state.unifiedInbox.replyScheduleRequest[emailId]?.error,

    fields: state.emailModal.fields,
    isDemoAccount: state.home.isDemoAccount,
  };
};

const mapDispatchToProps = {
  sendImageUploadRequest: (formData, onUploadProgress) =>
    imageUploadRequest({ formData, onUploadProgress }),
  sendReplyEmailRequest: (payload: ReplyEmailPayload) =>
    replyEmailRequest(payload),
  sendForwardEmailRequest: (payload: ForwardEmailPayload) =>
    forwardEmailRequest(payload),

  sendReplyDraftRequest: (payload: ReplyDraftPayload) =>
    replyDraftRequest(payload),
  sendReplyDraftDiscardRequest: (payload: ReplyDraftDiscardPayload) =>
    replyDraftDiscardRequest(payload),

  sendAttachmentUploadRequest: (
    payload: AttachmentUploadUniboxPayload,
    emailId: number,
  ) =>
    uploadAttachmentUniboxRequest({
      ...payload,
      emailId,
    }),
  sendDeleteAttachmentRequest: (
    payload: AttachmentDeleteUniboxPayload,
    emailId: number,
  ) =>
    deleteAttachmentUniboxRequest({
      ...payload,
      emailId,
    }),

  resetReplyDraftDiscardRequest: (emailId: number) =>
    resetReplyDraftDiscardRequest(emailId),
  resetReplyForwardDraftRequest: (emailId: number) =>
    resetReplyForwardDraftRequest(emailId),
  resetUploadAttachmentRequest: (emailId: number) =>
    resetUploadAttachmentUniboxRequest(emailId),
  resetDeleteAttachmentRequest: (emailId: number) =>
    resetDeleteAttachmentUniboxRequest(emailId),

  sendGetEmailThreadRequest: (payload: EmailThreadFilters) =>
    getEmailThreadRequest(payload),

  sendGetFieldsRequest: () => getFieldsRequest(),

  resetSequenceTimezoneData: (emailId: number) =>
    resetSequenceTimezoneData(emailId),
  sendReplyScheduleRequest: (payload: ReplySchedulePayload) =>
    replyScheduleRequest(payload),
  updateUniboxEmailScheduleData: (hashId: string, isDraftTab = false) =>
    updateUniboxEmailScheduleData({ hashId, isDraftTab }),
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export type IProps = ConnectedProps<typeof connector> &
  RouteComponentProps<unknown> & {
    label: string;
    hashId: string;
    emailThreadId: string;
    action: ReplyForwardEditorAction;
    t: (x: string) => React.ReactNode;
    onHideReplyEditor: VoidFunction;
    selectedEmail: ReplyForwardEmailPreview;
    showReplyEditor: boolean;
    userTimezone: string;
    unifiedSchedule?: UnifiedSchedule;
    sequenceId: number;
    uniboxFiltersFeed: UnifiedInboxFeed;
    shouldFetchEmailThreadsOnReplyAndForward?: boolean;
    showSubject?: boolean;
    subjectValue?: string;
    isEmailAccountDeleted?: boolean;
  };

export default withRouter(connector(ReplyForwardEditor));

export type StateValues = {
  to: string[];
  cc: string[];
  bcc: string[];
  content: string;
  attachments?: string;
};

export type StateErrors = { [P in keyof StateValues]: string };
export type StateDirty = { [P in keyof StateErrors]: boolean };

export type IState = {
  values: StateValues;
  errors: StateErrors;
  dirty: StateDirty;

  temporaryInputValues: {
    to: string;
    cc: string;
    bcc: string;
  };

  isCCInputVisible: boolean;
  isBCCInputVisible: boolean;

  showTemplates: boolean;
  showConfirmationModal: boolean;
  showScheduleModal: boolean;

  selectedTemplate?: Template;

  attachments: AttachmentType[];
  attachmentsMeta: AttachmentMetaDataType[];
  attachmentsSizeInBytes: number;

  isDraftSavePending: boolean;
  draftStatus: DraftStatus;
  isDraftSavedTextVisible: boolean;
  isEditorContentUnsaved: boolean;

  showGlobalRestrictionModal: boolean;
  globalRestrictionModalContent: {
    title: string;
    description: string;
  };
  isTemplateApplied: boolean;
};
